import { cloneDeep, isEmpty, set } from 'lodash';
import { Machine, ScratchpadBucket } from '../../../../api/graphql/generated';
import { robotRegistryService } from '../../../../robot/robot-registry.service';
import { getManufacturerByName } from '../../../../robot/robot.utils';
import { EnumType } from '../../../../util/enum';
import { logger } from '../../../../util/logger';
import {
  ApplicationDefaultParameterSetFormTags,
  ApplicationDefaultParameterSetTagToEquipmentMapping,
  ApplicationDefaultParameterSetTags,
  Flow,
  FlowItem,
  FlowItemParameterRef,
  FlowRule,
  FlowValidateResponse,
  TeachIOMapping,
  Tool,
} from '../model/flow.types';
import { FlowItemService, FlowRuleService, FlowService } from './service.types';

abstract class AbstractFlowService<
  FlowConfig,
  FlowSectionType extends EnumType = EnumType,
> implements FlowService<FlowConfig>
{
  constructor(
    protected tools: Tool[],
    protected flowItemService: FlowItemService<FlowConfig, FlowSectionType>,
    protected flowRuleService: FlowRuleService,
    protected flowValidationRules: FlowRule[],
    protected machine: Machine
  ) {}

  public async validate(flow: Flow): Promise<FlowValidateResponse> {
    const errors: FlowValidateResponse = this.flowRuleService.validateFlow(
      flow,
      this.flowValidationRules
    );

    return errors.length ? errors : undefined;
  }

  public async preSave(flow: Flow): Promise<Flow> {
    const cloned = cloneDeep(flow);

    cloned.items = this.flowItemService.preSave(
      cloned.config as FlowConfig,
      cloned.items,
      false
    );

    return cloned;
  }

  public getToolsForAssignedEquipment(): Tool[] {
    const equipment = this.getEquipmentName();
    const tools: Tool[] = [];

    const machineModel: string | null | undefined =
      this.machine.state?.runtimeInfo?.model;

    const machineManufacturer: string | null | undefined =
      this.machine.state?.runtimeInfo?.manufacturer;

    const conditionDetails = {
      machineId: this.machine.id,
      machineModel,
      equipment,
    };

    if (
      isEmpty(machineManufacturer) ||
      isEmpty(machineModel) ||
      isEmpty(equipment)
    ) {
      logger.info('info is incomplete', { conditionDetails });
      return tools;
    }

    if (this.tools.length === 0) {
      logger.info('tools array is empty', { conditionDetails });
    }

    let foundModelMatch = false;
    let foundEquipmentMatch = false;

    const manufacturer = getManufacturerByName(
      this.machine.state?.runtimeInfo?.manufacturer
    );

    if (!manufacturer) {
      logger.error(
        `Could not retrieve tools. Manufacturer not found in registry.`,
        {
          manufacturer: this.machine.state?.runtimeInfo?.manufacturer,
          conditionDetails,
        }
      );
      return tools;
    }

    for (const tool of this.tools) {
      const hasModel = tool.models.includes(machineModel?.toUpperCase() ?? '');
      const hasEquipment = tool.equipment.includes(equipment);

      if (hasModel) {
        foundModelMatch = true;
      }

      if (hasEquipment) {
        foundEquipmentMatch = true;
      }

      if (hasEquipment && hasModel) {
        tools.push(tool);
      }
    }

    if (!foundModelMatch) {
      logger.info('no tool found for model', { conditionDetails });
    }

    if (!foundEquipmentMatch) {
      logger.info('no tool found for equipment', { conditionDetails });
    }

    return cloneDeep(tools);
  }

  protected getDefaultIOMapping(): TeachIOMapping {
    const machineManufacturer: string | null | undefined =
      this.machine.state?.runtimeInfo?.manufacturer;

    const robotPlugin = robotRegistryService.getPlugin(machineManufacturer);

    if (!robotPlugin) {
      logger.warn(
        'Could not retrieve default IO mapping. Manufacturer not found in registry. Using fallback.',
        {
          machineId: this.machine.id,
          machineManufacturer,
        }
      );
      // Fallback to TOOL_1 for backward compatibility
      return {
        input: { capture: 'TOOL_1' },
        output: { light: 'TOOL_1' },
      };
    }

    return robotPlugin.getDefaultIOMapping(this.machine);
  }

  public mapApplicationDefaultParameterSetTagsToFlowConfig<FlowConfig>(
    flow: Flow<FlowConfig>,
    tags: ApplicationDefaultParameterSetTags
  ): Flow<FlowConfig> {
    const cloned = cloneDeep(flow);

    Object.entries(this.getApplicationDefaultParameterSetTagToConfig()).forEach(
      ([tag, key]) => {
        if (tags[tag] !== undefined) {
          set(cloned, `config.${key}`, tags[tag]);
        }
      }
    );

    return cloned;
  }

  public mapFlowConfigToApplicationDefaultParameterSetFormTags<FlowConfig>(
    flow: Flow<FlowConfig>
  ): ApplicationDefaultParameterSetFormTags {
    return Object.entries(
      this.getApplicationDefaultParameterSetTagToConfig()
    ).reduce((tags, [tagKey, configKey]) => {
      const val = flow.config[configKey as keyof FlowConfig];
      if (typeof val === 'string' || typeof val === 'number') {
        tags[tagKey] = val;
      }
      return tags;
    }, {} as ApplicationDefaultParameterSetFormTags);
  }

  public abstract getAvailableFlowItemParameterRefs(): FlowItemParameterRef[];
  public abstract getDefaultFlowConfig(): FlowConfig;
  public abstract getEquipmentName(): string;
  public abstract isFlowValidForTeaching(flow: Flow): Promise<boolean>;
  public abstract getApplicationDefaultParameterSetTagToConfig(): Record<
    string,
    string
  >;

  public abstract getApplicationDefaultParameterSetTagToEquipmentMapping(): ApplicationDefaultParameterSetTagToEquipmentMapping;
  public abstract getApplicationDefaultParameterSetImplicitTags(
    item: FlowItem<string>
  ): Record<string, unknown>;

  public abstract getApplicationDefaultParameterSetSubheadingFromBucket(
    bucket: ScratchpadBucket
  ): string;
}

export { AbstractFlowService };
